/** Display vocabulary only: callers own collection, confidence and freshness policy. */
const sourceDescriptions = {
  procurement: 'Requested through Procurement',
  connectwise: 'Imported from ConnectWise',
  itglue: 'Imported from IT Glue',
  autotask: 'Imported from Autotask',
  manual: 'Entered manually',
  runway: 'Created in Runway',
  'communications-migration': 'Collected by Communications Migration',
  ironscales: 'Collected by Ironscales',
  m365: 'Collected from Microsoft 365',
  'graph-api': 'Collected through Microsoft Graph',
  osquery: 'Collected through osquery',
  'agent-telemetry': 'Reported by Hyperion Agent',
  'cis-benchmark': 'Assessed against CIS benchmarks',
  'vendor-sync': 'Synced from vendor',
  'manual-entry': 'Entered manually',
  scheduler: 'Created by a scheduled job',
  correlation: 'Correlated by Hyperion',
  'ai-assistant': 'Suggested by AI',
  webhook: 'Received through a webhook',
  sse: 'Received through live updates',
  cache: 'Read from cached evidence',
};

export type ProvenanceSource = keyof typeof sourceDescriptions;

const capabilityNames: Record<string, string> = {
  procurement: 'Procurement',
  connectwise: 'ConnectWise',
  itglue: 'IT Glue',
  autotask: 'Autotask',
  manual: 'Manual entry',
  runway: 'Runway',
  'communications-migration': 'Communications Migration',
  ironscales: 'Ironscales',
  m365: 'Microsoft 365',
  'graph-api': 'Microsoft Graph',
  osquery: 'osquery',
  'agent-telemetry': 'Hyperion Agent',
  'cis-benchmark': 'CIS benchmarks',
  'vendor-sync': 'Vendor sync',
  'manual-entry': 'Manual entry',
  scheduler: 'Scheduled jobs',
  correlation: 'Hyperion correlation',
  'ai-assistant': 'AI assistant',
  webhook: 'Webhook',
  sse: 'Live updates',
  cache: 'Cached evidence',
  huntress: 'Huntress',

  'remote-access': 'Remote Access',
  'email-signatures': 'Email Signatures',
  'device-management': 'Device Management',
  'site-assessment': 'Site Assessment',
  'endpoint-runtime': 'Endpoint Runtime',
  'network-monitoring': 'Network Monitoring',
  'device-assessment': 'Device Assessment',
  'automation-scripts': 'Automation Scripts',
  'monitoring-alerts': 'Monitoring Alerts',
  'configuration-drift': 'Configuration Drift',
  'customer-lifecycle': 'Customer Lifecycle',
  'asset-lifecycle': 'Asset Lifecycle',
  'service-intelligence': 'Service Intelligence',
  'project-delivery': 'Project Delivery',
  'service-operations': 'Service Operations',
  'backup-management': 'Backup Management',
  'identity-lifecycle': 'Identity Lifecycle',
  'tenant-setup': 'Tenant Setup',
  'credential-health': 'Credential Health',
  'domain-events': 'Domain Events',
  'tenant-governance': 'Tenant Governance',
  'knowledge-management': 'Knowledge Management',
  'sales-pipeline': 'Sales Pipeline',
  'tenant-administration': 'Tenant Administration',
  'cloud-governance': 'Cloud Governance',
  cerberus: 'Cerberus',
};

// Historical evidence can retain old identifiers; translate only their display.
const legacySources: Record<string, string> = {
  thor: 'remote-access',
  loki: 'email-signatures',
  odin: 'device-management',
  freki: 'site-assessment',
  hermes: 'communications-migration',
  huginn: 'endpoint-runtime',
  muninn: 'network-monitoring',
  geri: 'device-assessment',
  tyr: 'automation-scripts',
  skuld: 'monitoring-alerts',
  heimdall: 'configuration-drift',
  poseidon: 'customer-lifecycle',
  apollo: 'asset-lifecycle',
  athena: 'service-intelligence',
  atlas: 'project-delivery',
  midas: 'procurement',
  zeus: 'service-operations',
  argus: 'backup-management',
  idunn: 'backup-management',
  janus: 'identity-lifecycle',
  hestia: 'tenant-setup',
  heracles: 'credential-health',
  bifrost: 'domain-events',
  medusa: 'tenant-governance',
  mimir: 'knowledge-management',
  triton: 'sales-pipeline',
  trident: 'sales-pipeline',
  celestine: 'tenant-administration',
  hades: 'cloud-governance',
};

export function describeSource(source: string): string {
  const key = source.trim().toLowerCase();
  const canonical = Object.prototype.hasOwnProperty.call(legacySources, key)
    ? legacySources[key]
    : key;
  if (Object.prototype.hasOwnProperty.call(sourceDescriptions, canonical)) {
    return sourceDescriptions[canonical as ProvenanceSource];
  }
  if (Object.prototype.hasOwnProperty.call(capabilityNames, canonical))
    return `Reported by ${capabilityNames[canonical]}`;
  return 'Unknown source';
}

export function describeRequiredSource(source: string): string {
  const key = source.trim().toLowerCase();
  const canonical = Object.prototype.hasOwnProperty.call(legacySources, key)
    ? legacySources[key]
    : key;
  const name = Object.prototype.hasOwnProperty.call(capabilityNames, canonical)
    ? capabilityNames[canonical]
    : 'Unknown integration';
  return `Requires ${name}`;
}
